import {
  type DeviceDetailDto,
  getDevicesAdminControllerGetDeviceQueryKey,
  getDevicesAdminControllerListDevicesQueryKey,
  getDevicesAdminControllerListPartnersQueryKey,
  useDevicesAdminControllerClearJobs,
  useDevicesAdminControllerClearStation,
  useDevicesAdminControllerCreateDevice,
  useDevicesAdminControllerSetPartner,
  useDevicesAdminControllerUpdateConfig,
} from '@artic/api-client';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';

/** Params-less list key — prefix-matches every filtered list query for invalidation. */
export const DEVICES_LIST_KEY = getDevicesAdminControllerListDevicesQueryKey();

/** Resolve a device's display name from the detail cache (for breadcrumbs). */
export function getCachedDeviceName(queryClient: QueryClient, deviceAuthId: string): string | null {
  const entries = queryClient.getQueriesData<DeviceDetailDto>({
    queryKey: getDevicesAdminControllerGetDeviceQueryKey(deviceAuthId),
  });
  for (const [, data] of entries) {
    if (data?.device_name) return data.device_name;
    if (data?.device_id) return data.device_id;
  }
  return null;
}

export function useCreateDevice() {
  const queryClient = useQueryClient();
  return useDevicesAdminControllerCreateDevice({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: DEVICES_LIST_KEY });
      },
    },
  });
}

export function useUpdateDeviceConfig() {
  const queryClient = useQueryClient();
  return useDevicesAdminControllerUpdateConfig({
    mutation: {
      onSuccess: (_data, variables) => {
        void queryClient.invalidateQueries({
          queryKey: getDevicesAdminControllerGetDeviceQueryKey(variables.deviceAuthId),
        });
        void queryClient.invalidateQueries({ queryKey: DEVICES_LIST_KEY });
      },
    },
  });
}

export function useClearDeviceStation() {
  const queryClient = useQueryClient();
  return useDevicesAdminControllerClearStation({
    mutation: {
      onSuccess: (_data, variables) => {
        void queryClient.invalidateQueries({
          queryKey: getDevicesAdminControllerGetDeviceQueryKey(variables.deviceAuthId),
        });
      },
    },
  });
}

export function useClearDeviceJobs() {
  const queryClient = useQueryClient();
  return useDevicesAdminControllerClearJobs({
    mutation: {
      onSuccess: (_data, variables) => {
        void queryClient.invalidateQueries({
          queryKey: getDevicesAdminControllerGetDeviceQueryKey(variables.deviceAuthId),
        });
      },
    },
  });
}

/** Set the device's sole owning partner (or null for an Admin Device). */
export function useSetDevicePartner() {
  const queryClient = useQueryClient();
  return useDevicesAdminControllerSetPartner({
    mutation: {
      onSuccess: (_data, variables) => {
        void queryClient.invalidateQueries({
          queryKey: getDevicesAdminControllerGetDeviceQueryKey(variables.deviceAuthId),
        });
        void queryClient.invalidateQueries({
          queryKey: getDevicesAdminControllerListPartnersQueryKey(variables.deviceAuthId),
        });
        void queryClient.invalidateQueries({ queryKey: DEVICES_LIST_KEY });
      },
    },
  });
}
